import LZString from "lz-string";

import { experiments } from "@/utils/experiment";
import { featureFlags } from "@/utils/feature-flags/feature-flag-manifest";

import { FeatureGateOverrideValues } from "../feature-gates/types";

export const getExperimentOverrides = (
    parsedRequestCookies: Record<string, string | undefined>
): FeatureGateOverrideValues => {
    const expOverride = "atlassian_exp_overrides";
    for (const cookieKey of Object.keys(parsedRequestCookies)) {
        if (cookieKey === expOverride && parsedRequestCookies[expOverride]) {
            return decompressCookie(parsedRequestCookies[expOverride]);
        }
    }
    return {}; // return empty overrides if none are found
};

export const getFeatureGateOverrides = (
    parsedRequestCookies: Record<string, string>
): FeatureGateOverrideValues => {
    const fgOverride = "atlassian_feature_gate_overrides";
    for (const cookieKey of Object.keys(parsedRequestCookies)) {
        if (cookieKey === fgOverride && parsedRequestCookies[fgOverride]) {
            return decompressCookie(parsedRequestCookies[fgOverride]);
        }
    }
    return {}; // return empty overrides if none are found
};

export const decompressCookie = (cookie: string) => {
    try {
        return JSON.parse(LZString.decompressFromBase64(cookie));
    } catch (e) {
        return {};
    }
};

export interface OverrideResult {
    finalExperimentOverrides: Record<
        string,
        Record<string, boolean | number | string>
    >;
    finalFeatureGateOverrides: Record<string, boolean>;
}

export const processExperimentAndFeatureGateOverrides = (
    parsedRequestCookies: Record<string, string | undefined>,
    userAgent: string,
    isPerfOnetrustEnabled: boolean
): OverrideResult => {
    const filteredParsedRequestCookies = Object.fromEntries(
        Object.entries(parsedRequestCookies).filter(
            ([_, value]) => value !== undefined
        )
    ) as Record<string, string>;
    const experimentOverrides = getExperimentOverrides(
        filteredParsedRequestCookies
    );
    const featureGateOverrides = getFeatureGateOverrides(
        filteredParsedRequestCookies
    );

    const pollinatorCheck = userAgent.includes("Pollinator");
    const criterionCheck = userAgent.includes("Criterion");

    let finalExperimentOverrides = experimentOverrides;
    let finalFeatureGateOverrides = featureGateOverrides;

    if (pollinatorCheck || criterionCheck) {
        // Merge cookie overrides with defaults, cookie overrides take precedence
        finalExperimentOverrides = Object.values(experiments).reduce(
            (acc, experiment) => {
                acc[experiment.experimentName] = {
                    [experiment.parameterName]: experiment.defaultValue,
                    ...(experimentOverrides[experiment.experimentName] || {})
                };
                return acc;
            },
            {} as Record<string, Record<string, boolean | number | string>>
        );
        finalFeatureGateOverrides = Object.entries(featureFlags).reduce(
            (acc, [, featureFlagObj]) => {
                acc[featureFlagObj.featureFlagName] =
                    featureGateOverrides[featureFlagObj.featureFlagName] ??
                    false;
                return acc;
            },
            {} as Record<string, boolean>
        );
        console.log(
            "Overriding ALL experiments to control and ALL feature gates to false, with cookie overrides applied"
        );
    } else if (!isPerfOnetrustEnabled) {
        // Only merge cookie overrides with defaults, cookie overrides do NOT take precedence
        finalExperimentOverrides = Object.values(experiments).reduce(
            (acc, experiment) => {
                acc[experiment.experimentName] = {
                    [experiment.parameterName]: experiment.defaultValue
                };
                return acc;
            },
            {} as Record<string, Record<string, boolean | number | string>>
        );
        console.log(
            "Overriding ALL experiments to control, ignoring cookie overrides"
        );
    }

    return {
        finalExperimentOverrides,
        finalFeatureGateOverrides
    };
};
